/**
 * TypeScript interfaces for Ground Truth feature
 * These interfaces mirror the backend models for type safety in the frontend
 */

export interface IGroundTruthVersion {
  version_number: number;
  timestamp?: string | Date;
  modified_at?: string | Date;
  validator_user_id?: string;
  modified_by?: string;
  modified_by_username?: string;
  change_summary?: string;
  data_snapshot?: {
    title: string;
    author?: string;
    publication_date?: string | Date;
    keywords: string[];
    summary?: string;
    validation_status: GroundTruthValidationStatus;
    confidence_scores: IConfidenceScores;
  };
  snapshot?: {
    metadata?: Record<string, any>;
    document_type?: string;
    title?: string;
    author?: string;
    publication_date?: string | Date;
    keywords?: string[];
    summary?: string;
    validation_status?: GroundTruthValidationStatus;
    confidence_scores?: IConfidenceScores;
  };
}

export interface IConfidenceScores {
  title?: number;
  author?: number;
  publication_date?: number;
  keywords?: number;
  summary?: number;
  overall?: number;
}

export type GroundTruthValidationStatus = 'Validated' | 'Pending' | 'Needs Review' | 'Failed';
export type DocumentType = 'PDF' | 'HTML' | 'XML' | 'DOCX' | 'Other';

export interface IMasterGroundTruth {
  _id: string;
  document_id: string;
  source_name: string;
  document_type: DocumentType;

  // Core Metadata Fields
  title: string;
  author?: string;
  publication_date?: string | Date;
  keywords: string[];
  summary?: string;
  source_url: string;

  // Tags for filtering (similar to Bonds)
  tags?: string[];

  // Validation Metadata
  validation_status: GroundTruthValidationStatus;
  confidence_scores: IConfidenceScores;

  // Versioning
  current_version: number;
  versions: IGroundTruthVersion[];

  // Audit Trail
  created_by: string;
  created_at: string | Date;
  updated_at: string | Date;
  last_modified_version: number;
}

// API Response Types
export interface GroundTruthListResponse {
  data: IMasterGroundTruth[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GroundTruthVersionHistoryResponse {
  current_version: number;
  versions: IGroundTruthVersion[];
  total: number;
}

export interface GroundTruthComparisonResponse {
  versionA: IGroundTruthVersion;
  versionB: IGroundTruthVersion;
  differences: Array<{
    field: string;
    versionA: { value: any; version: number };
    versionB: { value: any; version: number };
  }>;
}

export interface GroundTruthStatistics {
  total: number;
  byStatus: Record<GroundTruthValidationStatus, number>;
  byType: Record<DocumentType, number>;
  recentlyUpdated: Array<{
    document_id: string;
    source_name: string;
    updated_at: string | Date;
    validation_status: GroundTruthValidationStatus;
  }>;
}

// Form/Update Types
export interface GroundTruthUpdatePayload {
  title?: string;
  author?: string;
  publication_date?: Date | null;
  keywords?: string[];
  summary?: string;
  validation_status?: GroundTruthValidationStatus;
  confidence_scores?: Partial<IConfidenceScores>;
  change_summary?: string;
}

export interface GroundTruthCreatePayload {
  document_id: string;
  source_name: string;
  document_type: DocumentType;
  title: string;
  author?: string;
  publication_date?: Date;
  keywords: string[];
  summary?: string;
  source_url: string;
  validation_status?: GroundTruthValidationStatus;
  confidence_scores?: Partial<IConfidenceScores>;
}

// Filter Types for API requests
export interface GroundTruthFilters {
  page?: number;
  limit?: number;
  search?: string;
  document_type?: DocumentType;
  validation_status?: GroundTruthValidationStatus;
  source_name?: string; // Filter by source name (only for ground-truth, not master)
  tags?: string[]; // Filter by tags
  sortBy?: 'created_at' | 'updated_at' | 'document_id' | 'source_name';
  sortOrder?: 'asc' | 'desc';
  source?: 'master' | 'ground-truth' | 'all'; // Filter by collection source
}

// Utility functions for working with Ground Truth data
export function isValidated(groundTruth: IMasterGroundTruth): boolean {
  return groundTruth.validation_status === 'Validated';
}

export function needsReview(groundTruth: IMasterGroundTruth): boolean {
  return groundTruth.validation_status === 'Needs Review';
}

export function hasHighConfidence(scores: IConfidenceScores, threshold = 0.8): boolean {
  return (scores.overall || 0) >= threshold;
}

export function formatValidationStatus(status: GroundTruthValidationStatus): string {
  const statusMap: Record<GroundTruthValidationStatus, string> = {
    'Validated': 'Validated ✓',
    'Pending': 'Pending Review',
    'Needs Review': 'Needs Review ⚠',
    'Failed': 'Failed ✗'
  };
  return statusMap[status] || status;
}

export function getStatusColor(status: GroundTruthValidationStatus): string {
  const colorMap: Record<GroundTruthValidationStatus, string> = {
    'Validated': '#4CAF50',    // Green
    'Pending': '#FF9800',       // Orange
    'Needs Review': '#FFC107',  // Amber
    'Failed': '#F44336'         // Red
  };
  return colorMap[status] || '#757575';
}

export function getDocumentTypeIcon(type: DocumentType): string {
  const iconMap: Record<DocumentType, string> = {
    'PDF': '📄',
    'HTML': '🌐',
    'XML': '📋',
    'DOCX': '📝',
    'Other': '📎'
  };
  return iconMap[type] || '📎';
}